import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../components/Input";
import { VALIDATION_FIELD_MAPPING, WHO_COLLECTS_MAT } from "../../../../utils/appConstant";
import { Validation } from "../../../../interfaces/common";
import { Edit as EditInterface } from "../../../../interfaces/reservation.interface";
import TextArea from "../../../../components/TextArea";
import Switch from "../../../../components/Switch";
import Dropdown from "../../../../components/Dropdown";
import { AuthContext } from "../../../../contexts/AuthContext";
import { hasResourseAccess } from "../../../../utils/methods";
import { PermissionActionsEnum, PermissionModulesEnum } from "../../../../enums";

interface FormProps {
  view?: boolean;
  edit?: boolean;
  formData: EditInterface;
  validation: Validation;
  reviewedByOptions: any[];
  allReviewedByOptions: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  onConfigureClick?: () => void;
  onAccommodationInfoClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onGuestFeesInfoClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

const Form: React.FC<FormProps> = ({
  formData,
  view,
  edit,
  validation,
  reviewedByOptions,
  allReviewedByOptions,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
  onConfigureClick,
  onAccommodationInfoClick,
  onGuestFeesInfoClick,
}) => {
  const { t } = useTranslation();
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasActiveAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [
      { [PermissionModulesEnum.Reservation]: [PermissionActionsEnum.Active] },
    ],
  });

  return (
    <form className="card-body mb-6" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">
          1. {t('reservations.edit.pageTexts.form.sections.scheduleDetails')}
        </h6>

        <div className="col-md-6 col-lg-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="date"
              id={VALIDATION_FIELD_MAPPING.bookingDate}
              name={VALIDATION_FIELD_MAPPING.bookingDate}
              disabled={view}
              label={t('reservations.edit.pageTexts.form.bookingDate.label')}
              placeholder={t('reservations.edit.pageTexts.form.bookingDate.placeholder')}
              value={formData.bookingDate}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.bookingDate)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.bookingDate)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>
        <div className="col-md-6 col-lg-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="date"
              required
              id={VALIDATION_FIELD_MAPPING.checkInDate}
              name={VALIDATION_FIELD_MAPPING.checkInDate}
              max={formData.checkOutDate}
              disabled={view || edit}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.checkInDate)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.checkInDate)?.message ?? ''}
              label={t('reservations.edit.pageTexts.form.checkInDate.label')}
              placeholder={t('reservations.edit.pageTexts.form.checkInDate.placeholder')}
              value={formData.checkInDate}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.checkInDate)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.checkInDate)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>
        <div className="col-md-6 col-lg-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="date"
              required
              id={VALIDATION_FIELD_MAPPING.checkOutDate}
              name={VALIDATION_FIELD_MAPPING.checkOutDate}
              min={formData.checkInDate || undefined}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.checkOutDate)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.checkOutDate)?.message ?? ''}
              label={t('reservations.edit.pageTexts.form.checkOutDate.label')}
              placeholder={t('reservations.edit.pageTexts.form.checkOutDate.placeholder')}
              value={formData.checkOutDate}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.checkOutDate)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.checkOutDate)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>
        <div className="col-md-6 col-lg-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.nights}
              name={VALIDATION_FIELD_MAPPING.nights}
              disabled
              label={t('reservations.edit.pageTexts.form.nights')}
              value={formData.nights}
            />
          </div>
        </div>
      </div>
      <hr className="my-6 mx-n4" />
      <div className="row g-6 align-items-center">
        <div className="col-md-6">
          <h6 className="mb-0">
            2. {t('reservations.edit.pageTexts.form.sections.financialDetails')}
          </h6>
        </div>
        <div className="col-md-6 d-flex justify-content-end">
            {onConfigureClick && (
              <i
                className="ri-settings-5-line ri-24px text-primary cursor-pointer"
                onClick={onConfigureClick}
              />
            )}
        </div>
        <div className="row g-4">
          <div className="col-md-6 col-lg-3">
            <div className="border rounded h-100 shadow-sm">
              <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
                <h6 className="mb-0 text-primary fw-bold">{t('reservations.edit.pageTexts.form.sections.accommodation')}</h6>
                {onAccommodationInfoClick && (
                  <i
                    className="ri-information-line ri-18px text-primary cursor-pointer"
                    onClick={onAccommodationInfoClick}
                    id="accommodation-info-trigger"
                  />
                )}
              </div>
              <div className="p-3 pt-4">
                <div className="form-floating form-floating-outline mb-3">
                  <Input
                    type="text"
                    disabled={view}
                    value={formData.xNightRoomFees}
                    id={VALIDATION_FIELD_MAPPING.xNightRoomFees}
                    name={VALIDATION_FIELD_MAPPING.xNightRoomFees}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.xNightRoomFees)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.xNightRoomFees)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.xNightRoomFees.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.xNightRoomFees.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.xNightRoomFees)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.xNightRoomFees)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div className="form-floating form-floating-outline mb-3">
                  <Input
                    type="text"
                    disabled={view}
                    value={formData.discount}
                    id={VALIDATION_FIELD_MAPPING.discount}
                    name={VALIDATION_FIELD_MAPPING.discount}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.discount)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.discount)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.discount.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.discount.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.discount)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.discount)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div className="form-floating form-floating-outline">
                  <Input
                    type="text"
                    disabled
                    value={formData.perNightFees}
                    id={VALIDATION_FIELD_MAPPING.perNightFees}
                    name={VALIDATION_FIELD_MAPPING.perNightFees}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.perNightFees)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.perNightFees)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.perNightFees.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.perNightFees.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.perNightFees)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.perNightFees)}
                    onKeyDown={onKeyDown}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6 col-lg-3">
            <div className="border rounded h-100 shadow-sm">
              <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
                <h6 className="mb-0 text-primary fw-bold">{t('reservations.edit.pageTexts.form.sections.guestFees')}</h6>
                {onGuestFeesInfoClick && (
                  <i
                    className="ri-information-line ri-18px text-primary cursor-pointer"
                    onClick={onGuestFeesInfoClick}
                    id="guest-fees-info-trigger"
                  />
                )}
              </div>
              <div className="p-3 pt-4">
                <div className="form-floating form-floating-outline mb-3">
                  <Input
                    type="text"
                    disabled={view}
                    className={(formData.cleaningFees !== '' && Number(formData.cleaningFees) === 0) ? 'warning border-2' : ''}
                    value={formData.cleaningFees}
                    id={VALIDATION_FIELD_MAPPING.cleaningFees}
                    name={VALIDATION_FIELD_MAPPING.cleaningFees}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.cleaningFees)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.cleaningFees)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.cleaningFees.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.cleaningFees.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.cleaningFees)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.cleaningFees)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div className="form-floating form-floating-outline mb-3">
                  <Input
                    type="text"
                    disabled={view}
                    value={formData.linenFees}
                    id={VALIDATION_FIELD_MAPPING.linenFees}
                    name={VALIDATION_FIELD_MAPPING.linenFees}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.linenFees)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.linenFees)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.linenFees.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.linenFees.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.linenFees)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.linenFees)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div className="form-floating form-floating-outline">
                  <Input
                    type="text"
                    disabled={view}
                    value={formData.petFees}
                    id={VALIDATION_FIELD_MAPPING.petFees}
                    name={VALIDATION_FIELD_MAPPING.petFees}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.petFees)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.petFees)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.petFees.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.petFees.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.petFees)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.petFees)}
                    onKeyDown={onKeyDown}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6 col-lg-3">
            <div className="border rounded h-100 shadow-sm">
              <div className="p-3 border-bottom">
                <h6 className="mb-0 text-primary fw-bold">{t('reservations.edit.pageTexts.form.sections.passThoughTaxes')}</h6>
              </div>
              <div className="p-3 pt-4">
                <div className="form-floating form-floating-outline mb-3">
                  <Input
                    type="text"
                    disabled={view}
                    className={(formData.hstTax !== '' && (Number(formData.hstTax) === 0 || (formData.hstExpected != null && Number(formData.hstTax) < formData.hstExpected))) ? 'warning border-2' : ''}
                    value={formData.hstTax}
                    id={VALIDATION_FIELD_MAPPING.hstTax}
                    name={VALIDATION_FIELD_MAPPING.hstTax}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hstTax)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hstTax)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.hstTax.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.hstTax.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.hstTax)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.hstTax)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div className="form-floating form-floating-outline">
                  <Input
                    type="text"
                    disabled={view}
                    className={((formData.whoCollectsMat === WHO_COLLECTS_MAT.customer || formData.whoCollectsMat === WHO_COLLECTS_MAT.cohost) && formData.matTax !== '' && (Number(formData.matTax) === 0 || (formData.matTaxExpected != null && Number(formData.matTax) < formData.matTaxExpected))) ? 'warning border-2' : ''}
                    value={formData.matTax}
                    id={VALIDATION_FIELD_MAPPING.matTax}
                    name={VALIDATION_FIELD_MAPPING.matTax}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.matTax)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.matTax)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.matTax.label')}${formData.matTaxPercentage != null ? ` (${formData.matTaxPercentage} %)` : ''}`}
                    placeholder={t('reservations.edit.pageTexts.form.matTax.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.matTax)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.matTax)}
                    onKeyDown={onKeyDown}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6 col-lg-3 pe-0">
            <div className="border rounded h-100 shadow-sm">
              <div className="p-3 border-bottom">
                <h6 className="mb-0 text-primary fw-bold">{t('reservations.edit.pageTexts.form.sections.others')}</h6>
              </div>
              <div className="p-3 pt-4">
                <div className="form-floating form-floating-outline mb-3">
                  <Input
                    type="text"
                    disabled={view}
                    value={formData.hostFees}
                    id={VALIDATION_FIELD_MAPPING.hostFees}
                    name={VALIDATION_FIELD_MAPPING.hostFees}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hostFees)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hostFees)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.hostFees.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.hostFees.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.hostFees)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.hostFees)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div className="form-floating form-floating-outline mb-3">
                  <Input
                    type="text"
                    disabled={view}
                    className={formData.includeAdjustments === true ? 'success border-2' : ''}
                    value={formData.adjustments}
                    id={VALIDATION_FIELD_MAPPING.adjustments}
                    name={VALIDATION_FIELD_MAPPING.adjustments}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.adjustments)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.adjustments)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.adjustments.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.adjustments.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.adjustments)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.adjustments)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <div className="form-floating form-floating-outline">
                  <Input
                    type="text"
                    disabled={view}
                    value={formData.actualRevenue ?? ''}
                    id={VALIDATION_FIELD_MAPPING.actualRevenue}
                    name={VALIDATION_FIELD_MAPPING.actualRevenue}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.actualRevenue)}
                    errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.actualRevenue)?.message ?? ''}
                    label={`${t('reservations.edit.pageTexts.form.actualRevenue.label')} `}
                    placeholder={t('reservations.edit.pageTexts.form.actualRevenue.placeholder')}
                    onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.actualRevenue)}
                    onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.actualRevenue)}
                    onKeyDown={onKeyDown}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr className="my-6 mx-n4" />
      <div className="row g-6">
        <h6 className="mb-0">
          3. {t('reservations.edit.pageTexts.form.sections.otherDetails')}
        </h6>
        <div className="col-md-8">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              label={t('reservations.edit.pageTexts.form.notes.label')}
              placeholder={t('reservations.edit.pageTexts.form.notes.placeholder')}
              value={formData.notes}
              disabled={view}
              required={!formData.reviewed}
              rows={4}
              className="form-control-lg h-100"
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ''}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="d-flex align-items-center mb-6 gap-4">
            <div className="form-floating form-floating-outline">
              <Switch
                label={t('reservations.edit.pageTexts.form.reviewed.label')}
                name={VALIDATION_FIELD_MAPPING.reviewed}
                checked={!!formData.reviewed}
                disabled={view}
                onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.reviewed)}
              />
            </div>
            {formData.status === VALIDATION_FIELD_MAPPING.unBilled && hasActiveAccess && (
              <div className="form-floating form-floating-outline">
                <Switch
                  label={formData.isActive ? t('reservations.edit.pageTexts.form.active.activeLabel') : t('reservations.edit.pageTexts.form.active.inActiveLabel')}
                  name={VALIDATION_FIELD_MAPPING.isActive}
                  checked={!!formData.isActive}
                  disabled={view}
                  onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.isActive)}
                />
              </div>
            )}
          </div>
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.reviewedBy ? [formData.reviewedBy] : []}
              disabled={view}
              required={!formData.reviewed}
              options={(reviewedByOptions || []).map((item: any) => ({
                label: item.name,
                value: item.id
              }))}
              allOptions={(allReviewedByOptions || []).map((item: any) => ({
                label: item.name,
                value: item.id
              }))}
              id={VALIDATION_FIELD_MAPPING.reviewedBy}
              name={VALIDATION_FIELD_MAPPING.reviewedBy}
              searchText={dropdownSearchData.reviewedBy}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.reviewedBy)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.reviewedBy)?.message ?? ''}
              placeholder={t('reservations.edit.pageTexts.form.reviewedBy.placeholder')}
              label={t('reservations.edit.pageTexts.form.reviewedBy.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default Form;
